import React from "react";
import { mockData } from "../data/mockData";
import { Card, CardContent } from "./ui/card";

const AboutSection = ({ data }) => {
  const stats = data?.companyInfo?.stats || [];
  const testimonials = data?.testimonials || [];
  const benefits = data?.benefits || [];
  return (
    <section id="about" className="py-24 bg-gray-900">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Section Header */}
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-light mb-8">
              The Healthcare Era
            </h2>
            <div className="w-24 h-px bg-red-500 mx-auto mb-8"></div>
            <p className="text-xl text-white/80 max-w-4xl mx-auto leading-relaxed font-light">
              Retirement living is evolving beyond traditional care. Today's residents deserve 
              health experiences that promote independence, vitality, and joy. The new standard 
              is the integration of specialised healthcare with meaningful community connections.
            </p>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <h3 className="text-3xl md:text-4xl font-light mb-8 leading-tight">
                Independence is <span className="italic">not</span> a luxury, it's your right
              </h3>
              <p className="text-lg text-white/80 mb-8 leading-relaxed font-light">
                Our mission is simple: we put life in your years. We help you stay in your retirement village, 
                keep playing bowls, continue meeting friends for coffee, and be strong enough to pick up 
                your grandchildren. We believe in keeping you independent and living life on your terms.
              </p>
              <p className="text-lg text-white/80 leading-relaxed font-light">
                Every exercise session, every balance class, every strength programme is designed with one goal: 
                helping you continue doing what you love with the people you love. From golf courses to 
                grandchildren's weddings, we're here to ensure you don't miss a moment.
              </p>
            </div>
            
            <div className="space-y-6">
              {/* Community Photo */}
              <div className="relative rounded-lg overflow-hidden mb-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_senior-living-plus/artifacts/68v0v2oz_AWB%20Photo.jpg"
                  alt="Our team building genuine relationships with retirement village residents"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white text-sm font-light text-center">
                    Our team building genuine relationships with residents
                  </p>
                </div>
              </div>

              {stats.map((stat, index) => (
                <div 
                  key={stat.label}
                  className="flex items-center p-6 bg-black/40 rounded-lg border border-white/10 hover:border-red-500/30 transition-all duration-300 hover:scale-105"
                >
                  <div className="mr-6">
                    <div className="text-3xl font-light text-red-500">{stat.value}</div>
                  </div>
                  <div>
                    <p className="text-white/80 font-light">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Inline Testimonials in Benefits */}
          <div className="mb-20">
            <h3 className="text-3xl font-light text-center mb-12">With our help, our clients can...</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {benefits.map((benefit, index) => (
                <Card 
                  key={index}
                  className="bg-black/40 border-white/10 hover:border-red-500/30 transition-all duration-300 hover:scale-105 group"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-4 flex-shrink-0 group-hover:scale-150 transition-transform duration-300"></div>
                      <p className="text-white/80 font-light leading-relaxed">{benefit}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Mini Testimonials Carousel */}
            <div className="bg-gray-800/30 rounded-lg p-8">
              <h4 className="text-xl font-light text-center mb-8 text-white/90">What Our Residents Say</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {testimonials.slice(0, 4).map((testimonial) => (
                  <div key={testimonial.id} className="bg-black/40 rounded-lg p-6 border border-white/10 hover:border-orange-500/30 transition-all duration-300">
                    <div className="flex text-orange-500 mb-3 text-sm">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <span key={i}>★</span>
                      ))}
                    </div>
                    <p className="text-white/80 italic text-sm leading-relaxed font-light mb-4">
                      "{testimonial.quote}"
                    </p>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-white font-light text-sm">{testimonial.name}{testimonial.age ? `, ${testimonial.age}` : ''}</p>
                      </div>
                      <div className="text-xs bg-orange-500/20 text-orange-500 px-2 py-1 rounded">
                        {testimonial.programme}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mission Statement */}
          <div className="text-center">
            <p className="text-xl md:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed font-light italic">
              "At Reignite Health, we transform retirement living through remarkable health 
              experiences that residents love, promoting independence and vitality in their golden years."
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;